import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, IconButton, InputAdornment, TextField, Typography, Grid, Stack, FormControl, InputLabel, Select, OutlinedInput, Chip, MenuItem, Tooltip } from '@mui/material';
import { AgGridTable } from 'components/tables/AgGridTable';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { closeCustomModalDialog, openCustomModalDialog, setModalLoader } from '../../redux/slices/modalSlice';
import { useDebounce } from 'hooks/useDebounce';
import EditIcon from 'common/customIcons/edit';
import { deleteLegal, getLegalList, openSnackbar } from '../../redux/slices/common';
import { CloseRounded, RemoveRedEyeRounded } from '@mui/icons-material';
import SearchIcon from 'common/customIcons/search';
import CommonLayout from 'layouts/DashboardLayout';
import MDButton from 'components/MDButton';
import { formatTimestampToWithoutTime } from 'utilities/common';
import { useTheme } from '@emotion/react';
import { RemoveRedEye } from '@mui/icons-material';
import { formatTimestampToDateView } from 'utilities/common';
import DeleteIcon from 'common/customIcons/delete';
import FilterBlogs from 'common/CommonDrawer/blog/FilterBlog';
import { deleteBlog, getBlogList, migrateAuthor, migrateQuickEditApi } from '../../redux/apis/blogs';
import { capitalizeText } from 'utilities/common';
import { getPermissionsByMode } from 'utilities/common';
import { getData } from 'utilities/common';
import { hasSectionPermission } from 'utilities/common';
import CloseIcon from '@mui/icons-material/Close';
import Slide from '@mui/material/Slide';

import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import { convertToAmPm } from 'utilities/common';

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction='left' ref={ref} {...props} />;
});

export default function IndexBlog() {
    const theme = useTheme();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { blogId } = useParams()
    const productsRef = useRef([]); // Ref to store products data
    const pageRef = useRef(0);
    const searchTermRef = useRef('');
    const mainGridApiRef = useRef('');
    const [personName, setPersonName] = React.useState([]);
    const gridRefApi = useRef(null);
    const [searchTerm, setSearchTerm] = useState('');
    const { fetchingBlogs = false, blogList = {} } = useSelector(state => state.blogs);
    const [isLoading, setIsLoading] = useState(true);
    const blogFilterPayload = useRef({});
    const [notesData, setNotesData] = useState('');
    const { migrateAuthorsList = {}, fetchingAuthors } = useSelector(state => state.blogs);

    const deleteHandler = (blogId) => {
        dispatch(setModalLoader(true));
        dispatch(deleteBlog(blogId)).unwrap().then((res) => {
            showResponseMessage(res?.message, 'success');
            dispatch(closeCustomModalDialog());
            pageRef.current = 0;
            const dataSource = {
                getRows
            };
            mainGridApiRef.current.ensureIndexVisible(0, null);
            mainGridApiRef.current.setGridOption('datasource', dataSource);
        }).catch((err) => {
            dispatch(setModalLoader(false));
            showResponseMessage(err?.message, 'error');
        });
    };

    const showResponseMessage = (message, errortype) => {
        if (errortype === 'success') {
            dispatch(openSnackbar({ content: message, color: 'success', icon: 'check' }));
        }
        if (errortype === 'error') {
            dispatch(openSnackbar({ content: message, color: 'error', icon: 'warning' }));
        }
    };


    const compactCellStyle = {
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis'
    };
    const truncateText = (value = '', limit = 20) => {
        const text = String(value ?? '');
        if (text.length <= limit) return text;
        return `${text.slice(0, limit)}...`;
    };
    const renderTruncatedCell = (value) => {
        const text = value || '-';
        const shouldTruncate = text !== '-' && String(text).length > 20;
        const displayText = shouldTruncate ? truncateText(text, 20) : text;

        return shouldTruncate ? (
            <Tooltip title={text} arrow>
                <Typography variant='body2' noWrap>
                    {displayText}
                </Typography>
            </Tooltip>
        ) : (
            <Typography variant='body2' noWrap>
                {displayText}
            </Typography>
        );
    };

    const columnDefs = [
        {
            headerName: 'Title',
            field: 'title',
            // sortable: false,
            suppressMenu: false,
            minWidth: 240,
            flex: 1,
            cellRenderer: (props) => {
                return renderTruncatedCell(props?.data?.title);
            },
            wrapText: false,
            cellStyle: compactCellStyle,
            onCellClicked: (params) => {
                navigate(`/manages/blog/${params?.data?._id}/view`)
            },
        },
        {
            headerName: 'Category',
            field: 'category',
            sortable: true,
            suppressMenu: false,
            minWidth: 180,
            flex: 1,
            cellRenderer: (props) => {
                if (!props?.data?.category) return "-";
                const category = Array.isArray(props.data.category)
                    ? props.data.category.join(', ')
                    : props.data.category;
                return renderTruncatedCell(category);
            },
            wrapText: false,
            cellStyle: compactCellStyle
        },
        {
            headerName: 'Author',
            field: 'authorName',
            suppressMenu: false,
            minWidth: 140,
            sortable: true,
            flex: 1,
            wrapText: false,
            cellStyle: compactCellStyle,
            cellRenderer: (props) => {
                return renderTruncatedCell(props?.data?.author?.name);
            },
        },
        {
            headerName: 'State',
            field: 'state',
            suppressMenu: false,
            minWidth: 140,
            sortable: true,
            flex: 1,
            wrapText: false,
            cellStyle: compactCellStyle,
            cellRenderer: (props) => {
                return renderTruncatedCell(capitalizeText(props?.data?.state));
            },
        },
        {
            headerName: 'Created Date',
            field: 'createdDate',
            suppressMenu: false,
            // sortable: false,
            minWidth: 170,
            flex: 1,
            wrapText: false,
            cellStyle: compactCellStyle,
            cellRenderer: useCallback((props) => (
                props?.data?.createdDate ? formatTimestampToDateView(props?.data?.createdDate) : '-'
            ), []),
        },

        {
            headerName: 'Status',
            field: 'status',
            suppressMenu: false,
            sortable: true,
            minWidth: 140,
            flex: 1,
            wrapText: false,
            cellStyle: compactCellStyle,
            cellRenderer: useCallback((props) => {
                const status = props?.data?.scheduleBlogStatus ? "Scheduled" : capitalizeText(props?.value) || '-';
                const color = status === 'Scheduled' ? 'success.main' : status === 'Published' ? 'success.main' : 'warning.main';
                return (
                    status ?
                        <Box display="flex" alignItems="start" flexDirection="column">
                            <Typography variant='body1' color={color} fontSize={14}>
                                {status}
                            </Typography>
                            {
                                status === 'Scheduled' &&
                                <Typography variant='body1' color="#656575" fontSize={12}>
                                    {props?.data?.scheduleDate}&nbsp;{convertToAmPm(props?.data?.scheduleTime)}
                                </Typography>
                            }
                        </Box>
                        : "-"
                );
            }, []),
        },
        {
            headerName: 'Quick Edit',
            field: 'notes',
            suppressMenu: false,
            cellRenderer: (props) => {
                return <MDButton onClick={() => {
                    setNotesData(props?.data);
                }} size='small' startIcon={<EditIcon width='16px' height='16px' fontSize='small' />} variant='contained' color='primary' className='btn-primary-light'>
                    Quick Edit
                </MDButton>
            },
            minWidth: 180,
        },
        {
            headerName: 'Actions',
            sortable: false,
            cellRenderer: (props) => {
                // if (!props?.data?._id) return;
                return <Box display="inline-flex" gap={1} alignItems="center">
                    <IconButton color='primary' onClick={(e) => {
                        e.stopPropagation();
                        if (props?.data?.status === "published" && !props?.data?.scheduleBlogStatus) {
                            window.open(`${props?.data?.canonical}`)
                        } else {
                            navigate(`/manages/blog/${props.data._id}/view`)
                        }
                    }} size='small' variant="primary" sx={{ marginLeft: 1, }}>
                        <RemoveRedEye />
                    </IconButton>
                    <IconButton disabled={!canEdit} onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/manages/blog/${props.data._id}/edit`)
                    }} color='primary' size='small' variant="primary">
                        <EditIcon width='16px' height='16px' />
                    </IconButton>
                    {/* <IconButton disabled={!canDelete} color='error' size='small' variant='primary' sx={{ marginLeft: 1 }}
                        onClick={(e) => {
                            e.stopPropagation()
                            dispatch(openCustomModalDialog({ modalData: { deleteHandler, deleteId: props?.data?._id, deleteType: 'Blog' }, modal: 'DELETE_MODAL' }))
                        }}  >
                        <DeleteIcon width='16px' height='16px' />
                    </IconButton> */}
                </Box>;
            },
            cellStyle: { textAlign: 'right', justifyContent: 'end' },
            headerClass: 'right-aligned-cell-header',
            minWidth: 100,
            flex: 1,
            pinned: 'right'
        },
    ];

    function getSortInfo(data) {
        const sortInfo = {
            sortBy: '',
            sortOrder: ''
        };
        data.forEach(item => {
            sortInfo.sortBy = item.colId;
            sortInfo.sortOrder = item.sort === "asc" ? 'asc' : 'desc';
        });
        return sortInfo;
    }

    const getRows = async (params, value) => {
        gridRefApi.current = params;
        let filter = {};
        if (params?.sortModel.length) {
            filter = { ...getSortInfo(params?.sortModel) };
        }
        // Ensure that the 'state' property in blogFilterPayload.current (if present) is lowercased before using in API
        let filterPayload = { ...blogFilterPayload.current };
        if (filterPayload && Array.isArray(filterPayload.state)) {
            filterPayload.state = filterPayload.state.map(s => typeof s === 'string' ? s.toLowerCase() : s);
        }

        // Make the API call with filters
        dispatch(getBlogList({
            skip: pageRef.current,
            limit: 20,
            search: searchTermRef.current,
            ...(Array.isArray(value) && value.length > 0 ? { platform: value.join(",") } : {}),
            ...filter,
            ...filterPayload // Include the filter payload from the blog filter
        }))
            .unwrap().then(res => {
                const { blogs = [], total } = res;
                productsRef.current = blogs;

                // Handle the status on the frontend side
                const modifiedBlogs = blogs.map((item) => ({
                    ...item,
                }));
                // Pass the modified data to the grid
                params.successCallback(modifiedBlogs, total);

                // Update the page reference
                pageRef.current = pageRef.current + 20;
            }).catch((err) => {
                params.successCallback([], 0);
            });
    };

    const searchFn = (value) => {
        const dataSource = {
            getRows: (params) => getRows(params, value) // Pass the current filter payload
        };
        mainGridApiRef.current.ensureIndexVisible(0, null);
        mainGridApiRef.current.setGridOption('datasource', dataSource);
    };

    const onEditClicked = (row) => {
        navigate(`/manages/blog/edit`);
    };

    useEffect(() => {
        if (mainGridApiRef?.current) {
            if (fetchingBlogs) {
                mainGridApiRef.current.showLoadingOverlay();
            } else if (blogList?.total === 0) {
                mainGridApiRef.current.showNoRowsOverlay();
            }
            else {
                mainGridApiRef.current.hideOverlay();
            }
        }
    }, [blogList, fetchingBlogs]);

    const handleSearch = useDebounce(() => {
        pageRef.current = 0;
        searchFn();
    }, 500);

    const searchHandler = (searchValue) => {
        setSearchTerm(searchValue);
        searchTermRef.current = searchValue;
        handleSearch(searchValue);
    };

    const onRowClicked = useCallback((row) => {
        if (row?.colDef?.headerName !== 'Actions') {
            navigate(`/manages/static/details/${row?.data?.sectionType}?roleTab=hemp&heading=${row?.data?.heading}&slug=${row?.data?.slug}`)
        }
    }, []);
    const applyBlogFilter = (filters) => {
        // Reset pagination when applying new filters
        pageRef.current = 0;

        // Update the filter payload reference
        blogFilterPayload.current = filters;

        // Create new data source with updated filters
        const dataSource = {
            getRows: (params) => getRows(params, filters)
        };

        // Refresh the grid with new data
        if (mainGridApiRef.current) {
            mainGridApiRef.current.ensureIndexVisible(0, null);
            mainGridApiRef.current.setGridOption('datasource', dataSource);
        }
    };


    const getQuryValues = (data) => {
        blogFilterPayload.current = data;
        setIsLoading(false);


    };


    let localUser = getData('login-user-info')
    if (localUser) {
        localUser = JSON.parse(localUser)
    }

    const adminMode = localStorage.getItem("adminsMode") ? JSON.parse(localStorage.getItem("adminsMode")) : null;
    const permissionsForMode = getPermissionsByMode(localUser?.permissions, adminMode);

    const canCreate = hasSectionPermission(permissionsForMode, "Manage Website", "Create");
    const canEdit = hasSectionPermission(permissionsForMode, "Manage Website", "Edit/Update");
    const canView = hasSectionPermission(permissionsForMode, "Manage Website", "View Only");
    const canDelete = hasSectionPermission(permissionsForMode, "Manage Website", "Delete");

    useEffect(() => {
        dispatch(migrateAuthor({ skip: 0, limit: 100 })).unwrap().then(res => {
            // setMigrateAuthorsList(res?.data)
        }).catch(err => {
            console.error(err)
        })
    }, [])

    const submitHandler = () => {
        const payload = {
            author: {
                "name": notesData?.author?.name,
                "_id": notesData?.author?._id,
                slug: notesData?.author?.slug
            },
            title: notesData?.title,
            // scheduleBlogStatus: true,
            // scheduleDate: "2026-03-24",
            // scheduleTime: "06:00",
            // status: "published"
        }

        dispatch(migrateQuickEditApi({ data: payload, id: notesData?._id })).unwrap().then(res => {
            dispatch(openSnackbar({ content: res?.message, color: "success", icon: "check" }));
            setNotesData("")
            pageRef.current = 0;
            searchFn();
        }).catch(err => {
            console.error(err)
            dispatch(openSnackbar({ content: err?.message, color: "error", icon: "error" }));

        })
    }

    return (
        <CommonLayout>
            <Box p={{ xs: 2, sm: 3, lg: 4 }} className="row-cursor">
                <Grid container alignItems="center" rowSpacing={2} columnSpacing={2} mb={2}>
                    <Grid item xs={12} sm={6}>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography variant='h4'>Blog</Typography>
                        </Box>
                        <Box display="flex" justifyContent="space-between" alignItems="center" gap={1} mt={1}>
                            <Typography variant='body2' style={{ background: "#fcfcfc", flex: "1", padding: "8px 15px", borderRadius: "30px", fontWeight: "500" }}>All: {blogList?.total}</Typography>
                            <Typography variant='body2' style={{ background: "#fcfcfc", flex: "1", padding: "8px 15px", borderRadius: "30px", fontWeight: "500" }}>Published: {blogList?.publishedCount}</Typography>
                            <Typography variant='body2' style={{ background: "#fcfcfc", flex: "1", padding: "8px 15px", borderRadius: "30px", fontWeight: "500" }}>Draft: {blogList?.draftCount}</Typography>
                            <Typography variant='body2' style={{ background: "#fcfcfc", flex: "1", padding: "8px 15px", borderRadius: "30px", fontWeight: "500" }}>Scheduled: {blogList?.scheduledCount}</Typography>
                        </Box>

                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <Stack direction="row" justifyContent={{ xs: 'start', sm: 'end' }} flexWrap="wrap" alignItems="stretch" gap={2}>
                            <TextField className="custom-search-field" placeholder="Search" label="" value={searchTerm} onChange={(e) => searchHandler(e.target.value)}
                                sx={{ minWidth: "300px" }}
                                InputProps={{
                                    startAdornment: <InputAdornment position="start">
                                        <IconButton size='small'>
                                            <SearchIcon width='18px' height='18px' />
                                        </IconButton>
                                    </InputAdornment>,
                                    endAdornment: searchTerm?.length > 0 ? <InputAdornment>
                                        <IconButton onClick={() => searchHandler('')} size='small'>
                                            <CloseRounded />
                                        </IconButton>
                                    </InputAdornment> : null
                                }}
                            />
                            {
                                canCreate &&
                                <MDButton
                                    sx={{ minWidth: '160px' }}
                                    type='button'
                                    variant='contained'
                                    color='primary'

                                    onClick={() => { navigate(`/manages/blog/add`); }}
                                >
                                    {'+ Add'}
                                </MDButton>
                            }


                        </Stack>
                    </Grid>
                </Grid>
                <Grid item xs={12} lg={12}>
                    <FilterBlogs applyBlogFilter={applyBlogFilter} setIsLoading={setIsLoading} getQuryValues={getQuryValues} blogFilterPayload={blogFilterPayload?.current} />
                </Grid>
                {
                    canView &&
                    <AgGridTable col={columnDefs} getRows={getRows} mainGridApiRef={mainGridApiRef} pageRef={pageRef} isLoading={fetchingBlogs} style={{ height: '70vh' }} />
                }
            </Box>
            <Dialog open={Boolean(notesData)} TransitionComponent={Transition} keepMounted maxWidth='xs' fullWidth className="notes-modal"
                onClose={() => setNotesData('')}
                aria-describedby="alert-dialog-slide-description"
            >
                <DialogTitle id="draggable-dialog-title" display="flex" alignItems="center" justifyContent="space-between" mb={0.5}>
                    {"Quick Edit"}
                    <IconButton onClick={() => setNotesData('')} edge='end' size='small'>
                        <CloseIcon fontSize='medium' />
                    </IconButton>
                </DialogTitle>
                <DialogContent className='notes-modal-body'>
                    <DialogContentText id='alert-dialog-slide-description'>
                        <Grid item xs={12}>
                            <Grid container spacing={3}>
                                <Grid item xs={12} md={12}>
                                    <Typography variant="body2" fontWeight="medium" color="#2E2E3A" mb={1}>Title</Typography>
                                    <TextField
                                        // label="Title"
                                        className="form-input"
                                        fullWidth
                                        placeholder="Title"
                                        value={notesData?.title}
                                        onChange={(e) => {
                                            setNotesData((prev) => ({ ...prev, title: e.target.value }));
                                        }}
                                    // error={Boolean(errors?.name)}
                                    // helperText={errors?.name}
                                    />
                                </Grid>
                                <Grid item xs={12} md={12}>
                                    <Typography variant="body2" fontWeight="medium" color="#2E2E3A" mb={1}>Author</Typography>
                                    <Select
                                        value={notesData.author?.name || ""}
                                        onChange={(e) => {
                                            setNotesData((prev) => {
                                                const selectedAuthor = migrateAuthorsList?.data?.find(a => a.name === e.target.value);
                                                return {
                                                    ...prev,
                                                    author: selectedAuthor || e.target.value
                                                };
                                            });
                                        }} name="Author"
                                        label="Select Author"
                                        fullWidth
                                        className="form-select select-field-icon"
                                    >
                                        {migrateAuthorsList?.data?.map((role, idx) => {
                                            return (
                                                <MenuItem value={role?.name} key={idx}>
                                                    {role?.name}
                                                </MenuItem>
                                            );
                                        })}
                                    </Select>
                                </Grid>
                                <Grid item xs={12}>
                                    <Stack direction="row" gap={2} justifyContent="flex-end">
                                        <MDButton
                                            sx={{ minWidth: "160px" }}
                                            type="button"
                                            variant="outlined"
                                            color="dark"
                                            className="dark-outlined-btn"
                                            disabled={fetchingAuthors}
                                            onClick={() => setNotesData('')}
                                        >
                                            Cancel
                                        </MDButton>
                                        <MDButton
                                            sx={{ minWidth: "160px" }}
                                            type="button"
                                            variant="contained"
                                            color="primary"
                                            disabled={fetchingAuthors}
                                            onClick={submitHandler}
                                        >
                                            {"Save"}
                                        </MDButton>
                                    </Stack>
                                </Grid>
                            </Grid>
                        </Grid>
                    </DialogContentText>
                </DialogContent>
            </Dialog>

        </CommonLayout>
    );
}